import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';
import termsOfServiceMd from '../../content/terms-of-service.md?raw';

function parseMarkdown(md: string): string {
  return md
    // H1
    .replace(/^# (.+)$/gm, '<h1 class="text-4xl font-bold text-gray-900 mb-6 mt-8 first:mt-0">$1</h1>')
    // H2
    .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-semibold text-gray-900 mt-12 mb-4">$1</h2>')
    // H3
    .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-gray-900 mt-8 mb-3">$1</h3>')
    // HR
    .replace(/^---$/gm, '<hr class="my-8 border-gray-200" />')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    // Unordered list items
    .replace(/^- (.+)$/gm, '<li class="text-gray-700 ml-4 list-disc list-inside">$1</li>')
    // Wrap consecutive <li> in <ul>
    .replace(/(<li[\s\S]*?<\/li>\n?)+/g, (match) => `<ul class="space-y-2 mb-4">${match}</ul>`)
    // Blockquote
    .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-[#29B3E6] pl-4 italic text-gray-600 my-4">$1</blockquote>')
    // Links
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-[#29B3E6] hover:text-[#1a8bb8] hover:underline transition-colors">$1</a>')
    // Paragraphs (lines that are not already HTML tags)
    .replace(/^(?!<[a-z]).+$/gm, (line) => line.trim() ? `<p class="text-gray-700 leading-relaxed mb-4">${line}</p>` : '')
    // Remove extra blank lines
    .replace(/\n{3,}/g, '\n\n');
}

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const htmlContent = parseMarkdown(termsOfServiceMd);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title="Terms of Service – XeroVerifier Invoice Fraud Protection"
        description="Read XeroVerifier's terms of service. Understand your rights and responsibilities when using our Xero invoice verification and fraud protection service."
        canonical="/terms-of-service"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'XeroVerifier Terms of Service',
          description: 'Terms of service for XeroVerifier Xero invoice fraud protection.',
        }}
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ backgroundColor: '#29B3E6' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6 text-white">Terms of Service</h1>
          <p className="text-xl text-white/90">
            Please read these terms carefully before using our services.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 flex-grow bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
            <div
              className="markdown-content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>

          {/* Back to Home Link */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#29B3E6] hover:text-[#1a8bb8] font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}